import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Combobox, ETypeOfNotification, Modal, setNotification } from 'medere-ui-framework';
import { CashSummaryDTO, CashRegisterDTO, CashRegisterFinancialAccountDTO, CloseAndReleaseTransferItem } from '../../../dto/locationcash/LocationCashDTO';
import { FinancialAccountResponseDTO } from '../../../dto/CashClosingDTO';
import { getFinancialAccounts } from '../../../services/CashClosingController';
import { getAllCashRegisters, getCashRegisterFinancialAccounts, closeAndRelease } from '../../../services/LocationCashController';
import { parseApiError } from '../../../services/apiError';
import { formatMoney, hasDiscrepancy, isValidDeclaredAmount } from '../utils';
import useCashDiscrepancyConfirmation from '../useCashDiscrepancyConfirmation';
import CashDiscrepancyConfirm from './CashDiscrepancyConfirm';

interface CashRegisterOption {
    locationCashRegisterId: number;
    description: string;
    displayDescription: string;
}

interface CloseAndReleaseModalProps {
    visible: boolean;
    onHide: () => void;
    hospitalLocationId: number;
    locationDescription: string;
    cashSummary: CashSummaryDTO;
    onSuccess: () => void;
}

// Transfer pendiente (antes de enviar)
interface PendingTransfer {
    sourceAccount: CashRegisterFinancialAccountDTO;
    destinationAccount: FinancialAccountResponseDTO;
    amount: number;
    notes: string;
}

export default function CloseAndReleaseModal({
    visible,
    onHide,
    hospitalLocationId,
    locationDescription,
    cashSummary,
    onSuccess,
}: CloseAndReleaseModalProps) {
    const dispatch = useDispatch();

    // ── Cierre ──
    const [registers, setRegisters] = useState<CashRegisterOption[]>([]);
    const [selectedRegister, setSelectedRegister] = useState<CashRegisterOption | null>(null);
    const [declaredAmount, setDeclaredAmount] = useState<number | null>(null);
    const [closingObservations, setClosingObservations] = useState('');

    // ── Transferencias (se arman localmente antes de enviar) ──
    const [financialAccounts, setFinancialAccounts] = useState<FinancialAccountResponseDTO[]>([]);
    const [cashRegisterAccounts, setCashRegisterAccounts] = useState<CashRegisterFinancialAccountDTO[]>([]);
    const [pendingTransfers, setPendingTransfers] = useState<PendingTransfer[]>([]);
    const [selectedSourceAccount, setSelectedSourceAccount] = useState<CashRegisterFinancialAccountDTO | null>(null);
    const [selectedDestAccount, setSelectedDestAccount] = useState<FinancialAccountResponseDTO | null>(null);
    const [transferAmount, setTransferAmount] = useState<number | null>(null);
    const [transferNotes, setTransferNotes] = useState('');

    // ── Confirmar ──
    const [confirmed, setConfirmed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const discrepancy = useCashDiscrepancyConfirmation();

    const totalTransferred = pendingTransfers.reduce((sum, t) => sum + t.amount, 0);
    const remainingAfterTransfers = (declaredAmount ?? 0) - totalTransferred;


    // ── Load data on open ──
    useEffect(() => {
        if (!visible) return;
        let cancelled = false;

        // Reset
        setSelectedRegister(null);
        setDeclaredAmount(null);
        setClosingObservations('');
        setCashRegisterAccounts([]);
        setPendingTransfers([]);
        setSelectedSourceAccount(null);
        setSelectedDestAccount(null);
        setTransferAmount(null);
        setTransferNotes('');
        setConfirmed(false);
        discrepancy.reset();

        getAllCashRegisters()
            .then((data: CashRegisterDTO[]) => {
                if (cancelled) return;
                const options: CashRegisterOption[] = data
                    .filter(r => r.active)
                    .map(r => ({
                        locationCashRegisterId: r.locationCashRegisterId,
                        description: r.description,
                        displayDescription: r.locationDescription
                            ? `${r.description} — ${r.locationDescription}`
                            : r.description,
                    }));
                setRegisters(options);
                const match = options.find(o => o.locationCashRegisterId === cashSummary.locationCashRegisterId);
                if (match) setSelectedRegister(match);
            })
            .catch(console.error);

        getFinancialAccounts()
            .then(accounts => {
                if (cancelled) return;
                setFinancialAccounts(accounts);
            })
            .catch(console.error);

        return () => { cancelled = true; };
    }, [visible]);

    useEffect(() => {
        if (!visible || !selectedRegister) return;
        let cancelled = false;

        getCashRegisterFinancialAccounts(selectedRegister.locationCashRegisterId)
            .then(registerAccounts => {
                if (cancelled) return;
                const activeRegisterAccounts = registerAccounts.filter(account => account.active);
                setCashRegisterAccounts(activeRegisterAccounts);
                setSelectedSourceAccount(activeRegisterAccounts.find(account => account.isDefault) ?? null);
            })
            .catch(console.error);

        return () => { cancelled = true; };
    }, [visible, selectedRegister?.locationCashRegisterId]);

    // ── Add transfer to local list ──
    function handleAddTransfer() {
        if (!selectedSourceAccount || !selectedDestAccount || transferAmount == null || transferAmount <= 0) return;
        if (transferAmount > remainingAfterTransfers) {
            dispatch(setNotification({
                title: 'Monto inválido',
                desc: `La transferencia no puede superar el monto que queda disponible: ${formatMoney(remainingAfterTransfers)}`,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        setPendingTransfers(prev => [
            ...prev,
            {
                sourceAccount: selectedSourceAccount,
                destinationAccount: selectedDestAccount,
                amount: transferAmount,
                notes: transferNotes.trim(),
            },
        ]);
        setSelectedSourceAccount(cashRegisterAccounts.find(account => account.isDefault) ?? null);
        setSelectedDestAccount(null);
        setTransferAmount(null);
        setTransferNotes('');
    }

    function handleRemoveTransfer(index: number) {
        setPendingTransfers(prev => prev.filter((_, i) => i !== index));
    }

    // El checkbox repite el monto que queda en caja: al revisarlo hay que volver a confirmarlo
    function handleReview() {
        discrepancy.review();
        setConfirmed(false);
    }

    // Con la confirmación a la vista, ✕ / Esc equivalen a "Revisar el monto" y no cierran la operación
    function handleVisibleChange(value: boolean) {
        if (value || submitting) return;
        if (discrepancy.pending) handleReview();
        else onHide();
    }

    // ── Submit everything atomically ──
    async function handleSubmit(acceptDiscrepancy = false) {
        if (!selectedRegister || !isValidDeclaredAmount(declaredAmount)) {
            dispatch(setNotification({
                title: 'Datos incompletos',
                desc: 'Seleccionar una caja e ingresar un monto declarado mayor o igual a cero',
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            return;
        }
        setSubmitting(true);
        try {
            const transfers: CloseAndReleaseTransferItem[] = pendingTransfers.map(t => ({
                sourceFinancialAccountId: t.sourceAccount.financialAccountId,
                destinationFinancialAccountId: t.destinationAccount.financialAccount,
                amount: t.amount,
                notes: t.notes || undefined,
            }));

            const response = await closeAndRelease({
                hospitalLocationId,
                locationCashRegisterId: selectedRegister.locationCashRegisterId,
                declaredAmount,
                closingObservations: closingObservations.trim() || undefined,
                transfers: transfers.length > 0 ? transfers : undefined,
                outgoingObservations: closingObservations.trim() || undefined,
                acceptDiscrepancy,
            });

            dispatch(setNotification({
                title: 'Puesto liberado',
                desc: hasDiscrepancy(response.closing.differenceamount)
                    ? 'Se cerró la caja con una diferencia pendiente de justificación, se registraron las transferencias, se firmó el traspaso y se liberó el puesto'
                    : 'Se cerró la caja, se registraron las transferencias, se firmó el traspaso y se liberó el puesto',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            onSuccess();
        } catch (error) {
            const apiError = parseApiError(error);
            if (discrepancy.handleRejection(apiError)) return;
            dispatch(setNotification({
                title: 'No se pudo completar la operación',
                desc: apiError.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
        } finally {
            setSubmitting(false);
        }
    }

    // Cuentas ya usadas en transferencias pendientes
    const usedAccountIds = new Set(pendingTransfers.map(t => t.destinationAccount.financialAccount));
    const availableAccounts = financialAccounts.filter(fa =>
        fa.active
        && !usedAccountIds.has(fa.financialAccount)
        && fa.financialAccount !== selectedSourceAccount?.financialAccountId,
    );

    return (
        <Modal
            visible={visible}
            setVisible={handleVisibleChange}
            header={`Cerrar caja y liberar — ${locationDescription}`}
            width="w-large"
            closable={!submitting}
        >
            {discrepancy.pending ? (
                <CashDiscrepancyConfirm
                    confirming={submitting}
                    onReview={handleReview}
                    onConfirm={() => handleSubmit(true)}
                />
            ) : (
                <div className="flex flex-column gap-3">
                    {/* ── Info del puesto ── */}
                    <div className="handoff-receipt">
                        <h4 className="handoff-receipt-title">Datos del cierre</h4>
                        <div className="grid">
                            <div className="col-12">
                                <span style={{ fontSize: '13px', color: '#5A6676' }}>Puesto</span>
                                <p className="m-0 font-medium">{locationDescription}</p>
                            </div>
                        </div>
                    </div>

                    {/* ── Cierre ── */}
                    <div className="flex flex-column gap-3">
                        <div>
                            <Combobox
                                label="Caja *"
                                list={registers}
                                value={selectedRegister}
                                setValue={(v: CashRegisterOption | null) => setSelectedRegister(v)}
                                optionLabel="displayDescription"
                                placeholder="Seleccionar caja..."
                            />
                        </div>
                        <div className="flex flex-column gap-1">
                            <label htmlFor="close-release-declared-amount" className="font-medium" style={{ fontSize: '13px' }}>Monto declarado *</label>
                            <InputNumber
                                inputId="close-release-declared-amount"
                                inputRef={discrepancy.declaredAmountRef}
                                value={declaredAmount}
                                onValueChange={e => setDeclaredAmount(e.value ?? null)}
                                mode="currency"
                                currency="ARS"
                                locale="es-AR"
                                min={0}
                                disabled={submitting}
                            />
                        </div>
                        <div className="flex flex-column gap-1">
                            <label className="font-medium" style={{ fontSize: '13px' }}>Observaciones (opcional)</label>
                            <InputTextarea
                                value={closingObservations}
                                onChange={e => setClosingObservations(e.target.value)}
                                rows={2}
                                autoResize
                                placeholder="Observaciones del cierre..."
                                disabled={submitting}
                            />
                        </div>
                    </div>

                    {/* ── Transferencias ── */}
                    <div style={{
                        background: 'var(--surface-50, #f8f9fa)',
                        border: '1px solid var(--surface-200, #dee2e6)',
                        borderRadius: '8px',
                        padding: '16px',
                    }}>
                        <h4 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600 }}>
                            Transferencias a cuentas financieras
                        </h4>
                        <p style={{ fontSize: '12px', color: '#5A6676', margin: '0 0 12px' }}>
                            Opcional. Agregue las transferencias que desea realizar al cerrar la caja.
                        </p>

                        {/* Agregar transferencia */}
                        {availableAccounts.length > 0 && (
                            <div
                                style={{
                                    display: 'grid',
                                    alignItems: 'end',
                                    gap: '8px',
                                    gridTemplateColumns: 'minmax(160px, 1.3fr) minmax(200px, 1.8fr) minmax(130px, 1fr) minmax(150px, 1.4fr) 42px',
                                    width: '100%',
                                }}
                            >
                                    <div style={{ minWidth: 0 }}>
                                        <Combobox
                                            label="Cuenta origen"
                                            list={cashRegisterAccounts}
                                            value={selectedSourceAccount}
                                            setValue={(v: CashRegisterFinancialAccountDTO | null) => setSelectedSourceAccount(v)}
                                            optionLabel="financialAccountName"
                                            placeholder="Seleccionar cuenta..."
                                        />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <Combobox
                                            label="Cuenta destino"
                                            list={availableAccounts}
                                            value={selectedDestAccount}
                                            setValue={(v: FinancialAccountResponseDTO | null) => setSelectedDestAccount(v)}
                                            optionLabel="name"
                                            placeholder="Seleccionar cuenta..."
                                        />
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div className="flex flex-column gap-1">
                                            <label className="font-medium" style={{ fontSize: '13px' }}>Monto</label>
                                            <InputNumber
                                                value={transferAmount}
                                                onValueChange={e => setTransferAmount(e.value ?? null)}
                                                mode="currency"
                                                currency="ARS"
                                                locale="es-AR"
                                                disabled={submitting}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div className="flex flex-column gap-1">
                                            <label className="font-medium" style={{ fontSize: '13px' }}>Notas</label>
                                            <InputTextarea
                                                value={transferNotes}
                                                onChange={e => setTransferNotes(e.target.value)}
                                                rows={1}
                                                autoResize
                                                placeholder="Opcional"
                                                disabled={submitting}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex align-items-end justify-content-end">
                                        <Button
                                            icon="pi pi-plus"
                                            className="buttonMain3"
                                            onClick={handleAddTransfer}
                                            disabled={!selectedSourceAccount || !selectedDestAccount || transferAmount == null || transferAmount <= 0 || transferAmount > remainingAfterTransfers || submitting}
                                            tooltip="Agregar transferencia"
                                        />
                                    </div>
                            </div>
                        )}

                        {/* Lista de transferencias pendientes */}
                        {pendingTransfers.length > 0 && (
                            <div className="mt-3">
                                <DataTable value={pendingTransfers} size="small" stripedRows>
                                    <Column
                                        header="Cuenta origen"
                                        body={(row: PendingTransfer) => row.sourceAccount.financialAccountName}
                                    />
                                    <Column
                                        header="Cuenta destino"
                                        body={(row: PendingTransfer) => row.destinationAccount.name}
                                    />
                                    <Column
                                        header="Monto"
                                        body={(row: PendingTransfer) => formatMoney(row.amount)}
                                        style={{ width: '130px', textAlign: 'right' }}
                                    />
                                    <Column
                                        header="Notas"
                                        body={(row: PendingTransfer) => row.notes || '—'}
                                    />
                                    <Column
                                        header=""
                                        body={(_row: PendingTransfer, { rowIndex }: { rowIndex: number }) => (
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-text p-button-danger p-button-sm"
                                                onClick={() => handleRemoveTransfer(rowIndex)}
                                                disabled={submitting}
                                                style={{ padding: '4px 8px' }}
                                            />
                                        )}
                                        style={{ width: '50px', textAlign: 'center' }}
                                    />
                                </DataTable>
                                <div className="flex justify-content-end mt-2">
                                    <span style={{ fontSize: '13px', fontWeight: 600 }}>
                                        Total a transferir: {formatMoney(totalTransferred)}
                                        {remainingAfterTransfers > 0 && (
                                            <span style={{ color: '#5A6676', fontWeight: 400, marginLeft: '8px' }}>
                                                (queda {formatMoney(remainingAfterTransfers)} en caja)
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Confirmar ── */}
                    <div className="flex align-items-center gap-2 mt-1">
                        <Checkbox
                            inputId="close-release-confirm"
                            checked={confirmed}
                            onChange={(e) => setConfirmed(!!e.checked)}
                            disabled={submitting}
                        />
                        <label htmlFor="close-release-confirm" className="cursor-pointer" style={{ fontSize: '13px' }}>
                            Confirmo que dejo la caja con un monto de <strong>{formatMoney(remainingAfterTransfers)}</strong>
                            {' '}(monto declarado: {formatMoney(declaredAmount)})
                            {pendingTransfers.length > 0 && (
                                <> luego de realizar {pendingTransfers.length} transferencia{pendingTransfers.length > 1 ? 's' : ''} por <strong>{formatMoney(totalTransferred)}</strong></>
                            )}
                        </label>
                    </div>

                    <div className="flex justify-content-end gap-2 mt-2">
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            className="buttonMain3"
                            onClick={onHide}
                            disabled={submitting}
                        />
                        <Button
                            label="Cerrar, Firmar y Liberar"
                            icon="pi pi-check"
                            className="buttonMain"
                            onClick={() => handleSubmit()}
                            loading={submitting}
                            disabled={!confirmed || !selectedRegister || !isValidDeclaredAmount(declaredAmount)}
                        />
                    </div>
                </div>
            )}
        </Modal>
    );
}
